// ════════════════════════════════════════════════════════════════════
// CONSENT MODULE — client helpers
//
// Two callers:
//   1. The AGENT side (HealthConsentModule) uses generateToken(),
//      DEFAULT_CONSENT_TEXT, and sendConsentLink() to fire the
//      magic link off via SMS/email.
//   2. The CLIENT side (ConsentSignPage) uses deviceFingerprint()
//      to produce the unspoofable browser proof shipped with the
//      signature.
//
// Token shape: short UUIDv4 (no dashes) + a 6-char random suffix
// so the link is short enough to not break in SMS but still
// effectively unguessable.
// ════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

const CONSENT_BASE_URL = () => {
  try { return window.location.origin; } catch { return ''; }
};

// 32 chars of hex = 128 bits of entropy. Random-suffix-padded for
// a clean visual length without compromising security.
export function generateToken() {
  const bytes = new Uint8Array(16);
  (globalThis.crypto || window.crypto).getRandomValues(bytes);
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return hex;
}

// ── CMS Federally-Facilitated Marketplace (FFM) canonical consent ──
// The exact CMS-required language for ACA Marketplace enrollment
// consent. Source: CMS-published agent/broker permission template.
// {client_name} and {agent_name} are substituted into the text at
// row-create time so the saved disclosure reads naturally and
// matches the audit-trail snapshot.
//
// DO NOT REWORD THIS. Marketplace compliance audits check for the
// four numbered purposes verbatim. Agents who use a non-canonical
// form risk having the consent rejected.
export const MARKETPLACE_CONSENT_TEXT = `I, {client_name}, give my permission to {agent_name} to serve as the health insurance agent or broker for myself and my entire household if applicable, for purposes of enrollment in a Qualified Health Plan offered on the Federally Facilitated Marketplace. By consenting to this agreement, I authorize the above-mentioned Agent to view and use the confidential information provided by me in writing, electronically, or by telephone only for the purposes of one or more of the following:

1. Searching for an existing Marketplace application;
2. Completing an application for eligibility and enrollment in a Marketplace Qualified Health Plan or other government insurance affordability programs, such as Medicaid and CHIP or advance tax credits to help pay for Marketplace premiums;
3. Providing ongoing account maintenance and enrollment assistance, as necessary;
4. Responding to inquiries from the Marketplace regarding my Marketplace application.

I understand that the Agent will not use or share my personally identifiable information (PII) for any purposes other than those listed above. The Agent will ensure that my PII is kept private and safe when collecting, storing, and using my PII for the stated purposes above.

I confirm that the information I provide for entry on my Marketplace eligibility and enrollment application will be true to the best of my knowledge. I understand that I do not have to share additional personal information about myself or my health with my Agent beyond what is required on the application for eligibility and enrollment purposes. I understand that my consent remains in effect until I revoke it, and I may revoke or modify my consent at any time by contacting the agent.`;

export function fillConsentText(template, { clientName, agentName } = {}) {
  return String(template || '')
    .replace(/\{client_name\}/g, clientName || '[Client Name]')
    .replace(/\{agent_name\}/g, agentName || '[Agent Name]');
}

// Long-form sign-date string matching the CMS reference form layout:
// "Tuesday, 29 April 2025 16:56:40".
export function formatSignDateLong(d) {
  const date = d ? new Date(d) : new Date();
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day     = date.getDate();
  const month   = date.toLocaleDateString('en-US', { month: 'long' });
  const year    = date.getFullYear();
  const hh      = String(date.getHours()).padStart(2, '0');
  const mm      = String(date.getMinutes()).padStart(2, '0');
  const ss      = String(date.getSeconds()).padStart(2, '0');
  return `${dayName}, ${day} ${month} ${year} ${hh}:${mm}:${ss}`;
}

// Hardened defaults the agent can edit before send. Keep these
// compliance-safe baselines — the AGENT can soften, never harden,
// the text below.
//
// enrollment_call defaults to the CMS-canonical FFM text. The other
// scopes carry compact TCPA / AOR / HIPAA defaults.
export const DEFAULT_CONSENT_TEXT = {
  enrollment_call: MARKETPLACE_CONSENT_TEXT,
  sms_marketing:
    'I consent to receive SMS messages from my licensed agent and the agency above regarding health insurance plans, renewals, premium changes, and enrollment reminders. Message and data rates may apply. Frequency varies. Reply STOP to opt out. Reply HELP for help. See terms at https://sixfigureagentcrm.com/sms-terms.',
  plan_of_record:
    'I appoint the licensed agent named above as my Agent of Record (AOR) for my health insurance plan effective the date of this signature. This authorizes the agent to receive commissions, communicate with my carrier on my behalf regarding non-clinical matters, and assist me with renewals and changes. I can change my AOR at any time by request to my carrier.',
  hipaa:
    'I authorize my licensed agent and the agency above to discuss my health insurance plan, premium, and claims status with the family members or third parties I identify in writing. This is limited to plan administration; it does not extend to clinical health information.',
  general:
    'I authorize my licensed agent and the agency above to contact me about health insurance products and services. I can revoke this authorization at any time.',
};

export const CONSENT_SCOPE_LABEL = {
  enrollment_call: 'Enrollment / Quoting Consent',
  sms_marketing:   'SMS Marketing (TCPA / A2P)',
  plan_of_record:  'Plan of Record (AOR)',
  hipaa:           'HIPAA Authorization',
  general:         'General Consent',
};

// Build the full magic link the client opens to sign.
export function buildSignUrl(token) {
  return `${CONSENT_BASE_URL()}/consent/${token}`;
}

// Insert the consent row + record send metadata. Returns the row.
// Caller is responsible for actually delivering the link via
// SMS / email — this just persists the intent.
//
// The agency / agent identity block is snapshotted ON THE ROW so
// the saved consent is exactly what the client saw + signed,
// even if the agent later edits their settings.
export async function createConsent({
  ownerId,
  clientId = null,
  leadId = null,
  clientName,
  clientDob = null,
  clientEmail = null,
  clientPhone = null,
  planTypes = [],
  scope = 'enrollment_call',
  consentText = null,
  sentVia = 'sms',
  expiresInDays = 30,
  // Agency / Agent identity block (snapshotted).
  agencyName = null,
  agencyNpn = null,
  agentName = null,
  agentNpn = null,
  agentPhone = null,
  agentEmail = null,
  // Free-form structured fields for scenario-specific data (plan
  // name + carrier + effective date on plan_selection; APTC on
  // marketplace_consent; etc.). Saved as jsonb on the row.
  formData = {},
}) {
  if (!ownerId)     throw new Error('ownerId required.');
  if (!clientName)  throw new Error('clientName required.');

  const token   = generateToken();
  const expires = new Date(Date.now() + expiresInDays * 86400000).toISOString();

  // Fill {client_name} + {agent_name} into the template at create
  // time so the saved disclosure text reads naturally and the
  // audit-trail snapshot is the exact text the client saw.
  const rawText = consentText || DEFAULT_CONSENT_TEXT[scope] || DEFAULT_CONSENT_TEXT.general;
  const filled  = fillConsentText(rawText, { clientName, agentName });

  const row = {
    owner_id:      ownerId,
    client_id:     clientId,
    lead_id:       leadId,
    token,
    client_name:   clientName,
    client_dob:    clientDob,
    client_email:  clientEmail,
    client_phone:  clientPhone,
    plan_types:    planTypes,
    scope,
    consent_text:  filled,
    status:        'pending',
    sent_via:      sentVia,
    sent_at:       new Date().toISOString(),
    sent_to_phone: clientPhone,
    sent_to_email: clientEmail,
    expires_at:    expires,
    agency_name:   agencyName,
    agency_npn:    agencyNpn,
    agent_name:    agentName,
    agent_npn:     agentNpn,
    agent_phone:   agentPhone,
    agent_email:   agentEmail,
    form_data:     formData && typeof formData === 'object' ? formData : {},
  };
  const { data, error } = await supabase
    .from('health_consents')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return { ...data, signUrl: buildSignUrl(token) };
}

// Fire-and-forget revoke. Owner-only — RLS gates it.
export async function revokeConsent({ consentId, ownerId, reason = '' }) {
  if (!consentId) throw new Error('consentId required.');
  const { error } = await supabase
    .from('health_consents')
    .update({
      status:         'revoked',
      revoked_at:     new Date().toISOString(),
      revoked_by:     ownerId,
      revoked_reason: reason || null,
    })
    .eq('id', consentId);
  if (error) throw error;
  return true;
}

// Owner-side timeline read: every consent for a given client/lead.
export async function fetchConsentsForTarget({ clientId = null, leadId = null }) {
  if (!clientId && !leadId) return [];
  let q = supabase.from('health_consents').select('*').order('created_at', { ascending: false });
  if (clientId) q = q.eq('client_id', clientId);
  if (leadId)   q = q.eq('lead_id', leadId);
  const { data, error } = await q;
  if (error) {
    console.warn('[consent] fetch failed:', error.message);
    return [];
  }
  return data || [];
}

// Owner-side health portal feed: most recent N consents across all
// clients. Powers the "Pending consents" badge in the sidebar.
export async function fetchOwnerConsents({ status = null, limit = 100 } = {}) {
  let q = supabase.from('health_consents').select('*').order('created_at', { ascending: false }).limit(limit);
  if (status) q = q.eq('status', status);
  const { data } = await q;
  return data || [];
}

// Sweep stale pending consents to 'expired'. Returns the count.
// Called on Health Portal mount.
export async function sweepExpiredConsents() {
  const { data } = await supabase.rpc('expire_stale_consents');
  return Number(data) || 0;
}

// ── BROWSER FINGERPRINT (client-signing side) ───────────────────
// Lightweight unspoofable proof bundled with the signature: SHA-256
// of (userAgent + language + screen + timezone + colorDepth +
// hardwareConcurrency). Same browser on the same device produces
// the same hash; we DON'T want full-on canvas fingerprinting because
// (a) it's unreliable across browsers and (b) it crosses an
// ethical line for what's essentially a click-to-sign form.
export async function deviceFingerprint() {
  try {
    const parts = [
      navigator.userAgent || '',
      navigator.language || '',
      `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      String(window.screen?.colorDepth || 0),
      String(navigator.hardwareConcurrency || 0),
    ].join('|');
    const enc = new TextEncoder().encode(parts);
    const buf = await (globalThis.crypto || window.crypto).subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

// One-shot prompt + capture for geolocation. Resolves with the geo
// object or null — never rejects (we don't want a refused permission
// to break the signing flow).
export function captureGeolocation({ timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    setTimeout(() => finish(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => finish({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => finish(null),
      { timeout: timeoutMs, maximumAge: 60000, enableHighAccuracy: false }
    );
  });
}
